import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { FiSearch, FiFilter } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';
import MatchCard from '../components/MatchCard/MatchCard';
import Modal from '../components/Modal/Modal';
import Pagination from '../components/Pagination/Pagination';
import p from './Pages.module.css';

const LAMBDA_KPI_FIELDS = [
  { key: 'lambdaHome', label: 'λ Home' }, { key: 'lambdaAway', label: 'λ Away' }, { key: 'lambdaTotal', label: 'λ Total' },
  { key: 'lambdaHomeHt', label: 'λ H HT' }, { key: 'lambdaAwayHt', label: 'λ A HT' },
  { key: 'lambdaHomeSh', label: 'λ H 2H' }, { key: 'lambdaAwaySh', label: 'λ A 2H' },
  { key: 'lambdaHomeCorners', label: 'λ H Corners' }, { key: 'lambdaAwayCorners', label: 'λ A Corners' },
  { key: 'lambdaHomeHtCorners', label: 'λ H HT C' }, { key: 'lambdaAwayHtCorners', label: 'λ A HT C' },
  { key: 'lambdaHomeYc', label: 'λ H YC' }, { key: 'lambdaAwayYc', label: 'λ A YC' },
  { key: 'lambdaHomeHtYc', label: 'λ H HT YC' }, { key: 'lambdaAwayHtYc', label: 'λ A HT YC' },
  { key: 'lambdaHomeSot', label: 'λ H SOT' }, { key: 'lambdaAwaySot', label: 'λ A SOT' },
  { key: 'lambdaHomeHtSot', label: 'λ H HT SOT' }, { key: 'lambdaAwayHtSot', label: 'λ A HT SOT' },
];

const QUOTA_FIELDS = [
  { label: 'FT Margins & O/U', fields: [
    { key: 'margin1x2', label: 'Margin 1x2' }, { key: 'marginDoubleChance', label: 'Margin DC' },
    { key: 'marginFirstGoal', label: 'Margin FG' }, { key: 'ou25o', label: 'O/U 2.5 O' }, { key: 'ou25u', label: 'O/U 2.5 U' },
  ]},
  { label: 'HT 1x2 & O/U', fields: [
    { key: 'rhth', label: 'HT Home' }, { key: 'rhtd', label: 'HT Draw' }, { key: 'rhta', label: 'HT Away' },
    { key: 'htou15o', label: 'HT O/U 1.5 O' }, { key: 'htou15u', label: 'HT O/U 1.5 U' },
  ]},
  { label: 'European Handicaps', fields: [
    { key: 'eh10r1', label: 'EH 1:0 — 1' }, { key: 'eh10rx', label: 'EH 1:0 — X' }, { key: 'eh10r2', label: 'EH 1:0 — 2' },
    { key: 'eh01r1', label: 'EH 0:1 — 1' }, { key: 'eh01rx', label: 'EH 0:1 — X' }, { key: 'eh01r2', label: 'EH 0:1 — 2' },
  ]},
  { label: '2nd Half Margins', fields: [
    { key: 'marginSh1x2', label: '2H 1x2' }, { key: 'marginShDoubleChance', label: '2H DC' }, { key: 'marginShBtts', label: '2H BTTS' },
    { key: 'marginShOu05', label: '2H O/U 0.5' }, { key: 'marginShOu15', label: '2H O/U 1.5' }, { key: 'marginShOu25', label: '2H O/U 2.5' },
    { key: 'marginShEh10', label: '2H EH 1:0' }, { key: 'marginShEh01', label: '2H EH 0:1' },
  ]},
  { label: 'Special Markets', fields: [
    { key: 'marginHsh', label: 'HSH' }, { key: 'marginWtn', label: 'Win to Nil' }, { key: 'marginDnb', label: 'Draw No Bet' },
  ]},
  { label: 'Corners FT Margins', fields: [
    { key: 'marginCornersFt1x2', label: '1x2' }, { key: 'marginCornersFtDoubleChance', label: 'DC' },
    { key: 'marginCornersFtOu1', label: 'O/U 1' }, { key: 'marginCornersFtOu2', label: 'O/U 2' }, { key: 'marginCornersFtOu3', label: 'O/U 3' },
    { key: 'marginCornersFtEh10', label: 'EH 1:0' }, { key: 'marginCornersFtEh01', label: 'EH 0:1' }, { key: 'marginCornersFtEt', label: 'ET' },
  ]},
  { label: 'Corners 1H Margins', fields: [
    { key: 'marginCornersHt1x2', label: '1x2' }, { key: 'marginCornersHtDoubleChance', label: 'DC' },
    { key: 'marginCornersHtOu1', label: 'O/U 1' }, { key: 'marginCornersHtOu2', label: 'O/U 2' }, { key: 'marginCornersHtOu3', label: 'O/U 3' },
    { key: 'marginCornersHtEh10', label: 'EH 1:0' }, { key: 'marginCornersHtEh01', label: 'EH 0:1' }, { key: 'marginCornersHtBt', label: 'BT' },
  ]},
  { label: 'Yellow Cards FT Margins', fields: [
    { key: 'marginYcFt1x2', label: '1x2' }, { key: 'marginYcFtDoubleChance', label: 'DC' },
    { key: 'marginYcFtOu1', label: 'O/U 1' }, { key: 'marginYcFtOu2', label: 'O/U 2' }, { key: 'marginYcFtOu3', label: 'O/U 3' },
    { key: 'marginYcFtEh10', label: 'EH 1:0' }, { key: 'marginYcFtEh01', label: 'EH 0:1' }, { key: 'marginYcFtEt', label: 'ET' },
  ]},
  { label: 'Yellow Cards 1H Margins', fields: [
    { key: 'marginYcHt1x2', label: '1x2' }, { key: 'marginYcHtDoubleChance', label: 'DC' },
    { key: 'marginYcHtOu1', label: 'O/U 1' }, { key: 'marginYcHtOu2', label: 'O/U 2' }, { key: 'marginYcHtOu3', label: 'O/U 3' },
    { key: 'marginYcHtEh10', label: 'EH 1:0' }, { key: 'marginYcHtEh01', label: 'EH 0:1' }, { key: 'marginYcHtEt', label: 'ET' },
  ]},
  { label: 'SOT FT Margins', fields: [
    { key: 'marginSotFt1x2', label: '1x2' }, { key: 'marginSotFtDoubleChance', label: 'DC' },
    { key: 'marginSotFtOu1', label: 'O/U 1' }, { key: 'marginSotFtOu2', label: 'O/U 2' }, { key: 'marginSotFtOu3', label: 'O/U 3' },
    { key: 'marginSotFtEh10', label: 'EH 1:0' }, { key: 'marginSotFtEh01', label: 'EH 0:1' }, { key: 'marginSotFtEt', label: 'ET' },
  ]},
  { label: 'SOT 1H Margins', fields: [
    { key: 'marginSotHt1x2', label: '1x2' }, { key: 'marginSotHtDoubleChance', label: 'DC' },
    { key: 'marginSotHtOu1', label: 'O/U 1' }, { key: 'marginSotHtOu2', label: 'O/U 2' }, { key: 'marginSotHtOu3', label: 'O/U 3' },
    { key: 'marginSotHtEh10', label: 'EH 1:0' }, { key: 'marginSotHtEh01', label: 'EH 0:1' }, { key: 'marginSotHtBt', label: 'BT' },
  ]},
];

export default function CreateClasico() {
  const api = useApi();
  const toast = useToast();
  const PAGE_SIZE = 20;
  const [matches, setMatches] = useState([]);
  const [search, setSearch] = useState('');
  const [picks, setPicks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [defaultMargins, setDefaultMargins] = useState({});
  const [quota, setQuota] = useState({});
  const [previewData, setPreviewData] = useState(null); // { lambdas, quota }
  const [previewing, setPreviewing] = useState(false);
  const previewTimer = useRef(null);
  const [mPage, setMPage] = useState(1); const [mPages, setMPages] = useState(1); const [mTotal, setMTotal] = useState(0);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().slice(0, 10)); const [dateTo, setDateTo] = useState('');

  const buildQ = useCallback((page) => {
    let q = `?status=draft,pending&page=${page}&size=${PAGE_SIZE}`;
    if (dateFrom) q += `&createdFrom=${dateFrom}T00:00:00`;
    if (dateTo) q += `&createdTo=${dateTo}T23:59:59`;
    return q;
  }, [dateFrom, dateTo]);

  const loadMatches = useCallback(async (page = 1) => {
    try {
      const r = await api.get(`/v1/match/${buildQ(page)}`);
      if (r?.items) { setMatches(r.items); setMPage(r.page); setMPages(r.pages); setMTotal(r.total); }
      else if (Array.isArray(r)) setMatches(r);
    } catch {}
  }, [api, buildQ]);

  useEffect(() => {
    loadMatches(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  useEffect(() => {
    api.get('/v1/default-margins/').then(dm => { if (dm) setDefaultMargins(dm); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!search) return matches;
    const l = search.toLowerCase();
    return matches.filter(m => m.teams?.some(t => t.name.toLowerCase().includes(l)));
  }, [matches, search]);

  const handleTeamClick = (matchId, team) => {
    setPicks(prev => {
      const exists = prev.findIndex(s => s.matchId === matchId && s.teamId === team.id);
      if (exists !== -1) return prev.filter((_, i) => i !== exists);
      const without = prev.filter(s => s.matchId !== matchId);
      if (without.length >= 2) return prev;
      const side = (() => { const r = team.side; if (r === 1 || r === 'Home' || r === 'home') return 'home'; if (r === 2 || r === 'Away' || r === 'away') return 'away'; return String(r).toLowerCase(); })();
      return [...without, { matchId, teamId: team.id, teamName: team.name, teamSide: side }];
    });
  };

  const homePick = picks[0] || null;
  const awayPick = picks[1] || null;

  const openModal = () => {
    if (!homePick || !awayPick) { toast.error('Pick 2 teams from the list'); return; }
    const m = defaultMargins || {};
    const q = {};
    // Populate all quota fields from defaults — iterate over all QUOTA_FIELDS sections
    QUOTA_FIELDS.forEach(section => {
      section.fields.forEach(f => {
        // For margin keys, try direct match from defaultMargins
        q[f.key] = m[f.key] ?? '';
      });
    });
    // Override specific quota fields that map from margin defaults
    q.margin1x2 = m.margin1x2 ?? ''; q.marginDoubleChance = m.marginDoubleChance ?? ''; q.marginFirstGoal = m.marginFirstGoal ?? '';
    q.ou25o = m.marginOu25 ?? ''; q.ou25u = m.marginOu25 ?? '';
    q.rhth = m.marginHt1x2 ?? ''; q.rhtd = m.marginHt1x2 ?? ''; q.rhta = m.marginHt1x2 ?? '';
    q.htou15o = m.marginHtOu15 ?? ''; q.htou15u = m.marginHtOu15 ?? '';
    q.eh10r1 = m.marginEh10 ?? ''; q.eh10rx = m.marginEh10 ?? ''; q.eh10r2 = m.marginEh10 ?? '';
    q.eh01r1 = m.marginEh01 ?? ''; q.eh01rx = m.marginEh01 ?? ''; q.eh01r2 = m.marginEh01 ?? '';
    setQuota(q);
    setShowModal(true);
  };

  const handleCreate = async () => {
    const pn = (v) => {
      if (v === '' || v == null) return null;
      const n = Number(String(v).replace(',', '.').trim());
      return Number.isFinite(n) ? n : null;
    };
    // Only these keys are accepted inside POST /v1/clasico/ "quota" block.
    // Everything else (the rest of the 77 margins) must be sent via
    // PUT /v1/clasico/{id}/margins + POST /recalculate after creation.
    const CREATE_QUOTA_ALLOWED = new Set([
      'margin1x2', 'marginDoubleChance', 'marginFirstGoal',
      'ou25o', 'ou25u',
      'rhth', 'rhtd', 'rhta',
      'htou15o', 'htou15u',
      'eh10r1', 'eh10rx', 'eh10r2',
      'eh01r1', 'eh01rx', 'eh01r2',
    ]);
    const parsedQuota = {};
    const parsedMargins = {};
    for (const [k, v] of Object.entries(quota)) {
      const num = pn(v);
      if (num == null) continue;
      if (CREATE_QUOTA_ALLOWED.has(k)) parsedQuota[k] = num;
      else if (k.startsWith('margin')) parsedMargins[k] = num;
    }
    try {
      const created = await api.post('/v1/clasico/', {
        matchTime: new Date().toISOString(),
        matchAId: homePick.matchId, matchBId: awayPick.matchId,
        teamSideA: homePick.teamSide, teamSideB: awayPick.teamSide,
        homeSelector: 'a', quota: parsedQuota,
      });
      const newId = created?.id;
      if (newId && Object.keys(parsedMargins).length > 0) {
        try {
          await api.put(`/v1/clasico/${newId}/margins`, parsedMargins);
          await api.post(`/v1/clasico/${newId}/recalculate`, {});
        } catch (e) {
          toast.error(`Created, but margin update failed: ${e.message || 'unknown'}`);
        }
      }
      toast.success('Clasico created successfully');
      setPicks([]); setShowModal(false); setPreviewData(null);
    } catch (err) { toast.error(err.message || 'Failed to create clasico'); }
  };

  // Debounced preview fetch when modal open and quota/picks change
  useEffect(() => {
    if (!showModal || !homePick || !awayPick) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      const pn = (v) => {
        if (v === '' || v == null) return null;
        const n = Number(String(v).replace(',', '.').trim());
        return Number.isFinite(n) ? n : null;
      };
      const margins = {};
      Object.entries(quota).forEach(([k, v]) => { if (k.startsWith('margin')) { const n = pn(v); if (n != null) margins[k] = n; } });
      setPreviewing(true);
      try {
        const res = await api.post('/v1/clasico/preview', {
          matchAId: homePick.matchId, matchBId: awayPick.matchId,
          teamSideA: homePick.teamSide, teamSideB: awayPick.teamSide,
          homeSelector: 'a', margins,
        });
        if (res) setPreviewData(res);
      } catch { /* preview endpoint may not exist yet — silently ignore */ }
      finally { setPreviewing(false); }
    }, 350);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, homePick, awayPick, quota]);

  return (
    <div className={p.page}>
      <div className={p.pageHeader}>
        <h1 className={p.pageTitle}>Create Clasico</h1>
        <p className={p.pageSubtitle}>Pick two teams from different matches to create a clasico</p>
      </div>

      <div className={p.toolbar}>
        <div className={p.toolbarFilters} style={{ gap: 8, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-muted)' }} />
            <input className={p.searchInput} style={{ paddingLeft: 32 }} placeholder="Search by team…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button onClick={() => setShowDateFilter(v => !v)} className={p.btnOutline} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', padding: '5px 12px' }}>
            <FiFilter size={13} /> {showDateFilter ? 'Hide' : 'Date Filter'}
          </button>
          {showDateFilter && <>
            <input type="date" className={p.formInput} style={{ width: 140, fontSize: '0.78rem', padding: '5px 8px' }} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setMPage(1); }} />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>to</span>
            <input type="date" className={p.formInput} style={{ width: 140, fontSize: '0.78rem', padding: '5px 8px' }} value={dateTo} onChange={e => { setDateTo(e.target.value); setMPage(1); }} />
            {(dateFrom || dateTo) && <button className={p.btnOutline} style={{ fontSize: '0.72rem', padding: '4px 10px' }} onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>}
          </>}
        </div>
      </div>

      <div className={p.list} style={{ maxHeight: 'calc(70vh - 120px)' }}>
        {filtered.length > 0 ? filtered.map(m => (
          <MatchCard key={m.id} match={m} onTeamClick={handleTeamClick} selectedTeams={picks.map(pk => ({ matchId: pk.matchId, teamId: pk.teamId }))} />
        )) : (
          <div className={p.emptyState}><div className={p.emptyText}>No matches available</div></div>
        )}
      </div>
      <Pagination page={mPage} pages={mPages} total={mTotal} size={PAGE_SIZE} onPageChange={pg => { setMPage(pg); loadMatches(pg); }} />

      {/* Pick bar */}
      <div className={p.pickBar}>
        <div className={p.pickSlot}>
          <div className={p.pickLabel}>Home</div>
          <div className={homePick ? p.pickTeam : `${p.pickTeam} ${p.pickEmpty}`}>
            {homePick ? homePick.teamName : 'Pick first team'}
          </div>
        </div>
        <div className={p.pickVs}>vs</div>
        <div className={p.pickSlot}>
          <div className={p.pickLabel}>Away</div>
          <div className={awayPick ? p.pickTeam : `${p.pickTeam} ${p.pickEmpty}`}>
            {awayPick ? awayPick.teamName : 'Pick second team'}
          </div>
        </div>
        <button className={p.btnPrimary} onClick={openModal}>Create Clasico</button>
      </div>

      <Modal open={showModal} onClose={() => { setShowModal(false); setPreviewData(null); }} title="Create Clasico" wide
        footer={<button className={p.btnPrimary} onClick={handleCreate}>Create Clasico</button>}>
        <div className={p.scoreboard} style={{ marginBottom: 20 }}>
          <div className={p.scoreTeam}><span className={p.scoreTeamName}>{homePick?.teamName}</span><span className={p.scoreTeamSide}>Home</span></div>
          <div className={p.scoreMain}>vs</div>
          <div className={p.scoreTeam}><span className={p.scoreTeamName}>{awayPick?.teamName}</span><span className={p.scoreTeamSide}>Away</span></div>
        </div>

        {/* Lambdas KPI strip (live preview) */}
        {previewData?.lambdas && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>Lambdas (preview)</h4>
              {previewing && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>updating…</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 6 }}>
              {LAMBDA_KPI_FIELDS.map(f => {
                const v = previewData.lambdas[f.key];
                return (
                  <div key={f.key} style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{f.label}</div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: v == null ? 'var(--text-muted)' : 'var(--text-primary)' }}>{v == null ? '—' : Number(v).toFixed(3)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {QUOTA_FIELDS.map(section => (
          <div key={section.label} style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 10, color: 'var(--text-secondary)' }}>{section.label}</h4>
            <div className={p.quotaGrid}>
              {section.fields.map(f => (
                <div key={f.key} className={p.quotaField}>
                  <label className={p.quotaLabel}>{f.label}</label>
                  <input className={p.quotaInput} type="text" value={quota[f.key] ?? ''} onChange={(e) => setQuota(prev => ({ ...prev, [f.key]: e.target.value }))} />
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Computed quotas grid — from preview */}
        {previewData?.quota && (
          <div style={{ marginTop: 24 }}>
            <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 10, color: 'var(--text-secondary)' }}>Computed Quotas (preview)</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6, fontSize: '0.72rem' }}>
              {Object.keys(previewData.quota)
                .filter(k => !k.endsWith('Fair') && !k.endsWith('Prob') && !k.endsWith('Book') && !k.endsWith('Line') && previewData.quota[k] != null)
                .sort()
                .map(k => {
                  const book = previewData.quota[k];
                  const fair = previewData.quota[`${k}Fair`];
                  const prob = previewData.quota[`${k}Prob`];
                  const isNull = book == null;
                  return (
                    <div key={k} style={{ padding: '5px 8px', borderRadius: 5, background: isNull ? 'transparent' : 'var(--bg-secondary)', border: '1px solid var(--border-color)', opacity: isNull ? 0.4 : 1 }}>
                      <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', fontWeight: 600 }}>{k}</div>
                      <div style={{ display: 'flex', gap: 6, fontVariantNumeric: 'tabular-nums' }}>
                        <span title="book">{book == null ? '—' : Number(book).toFixed(2)}</span>
                        {fair != null && <span style={{ color: 'var(--text-muted)' }} title="fair">f:{Number(fair).toFixed(2)}</span>}
                        {prob != null && <span style={{ color: 'var(--accent)' }} title="prob">p:{Number(prob).toFixed(3)}</span>}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

